import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import React, { useState } from "react";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import { isArmArchitecture } from "utils/architecture";
import BaseContainerSelector from "../ContainerSelector/BaseContainerSelector";

interface FeaturedContainerVersionParams {
  pythonVersion?: string;
  cudaVersion?: string;
}

interface DrawerContainerSelectorProps {
  selectedContainer: ContainerSelected;
  setSelectedContainer: (
    container: ContainerSelected,
    featuredContainerParams?: FeaturedContainerVersionParams,
  ) => void;
  pythonVersion: string;
  setPythonVersion: (pythonVersion: string) => void;
  cudaVersion: string;
  setCudaVersion: (cudaVersion: string) => void;
  closeDrawer: () => void;
  lifecycleScriptTab: "upload" | "paste";
  setLifecycleScriptTab: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  setLifecycleScriptFile: (file: File | null) => void;
  autoFocusScript?: boolean;
  selectedGpuName?: string;
  selectedInstanceArchitecture?: string;
  showRapidsOption?: boolean;
  disableKubernetes?: boolean;
  kubernetesDisabledReason?: string;
}

const DrawerContainerSelector: React.FC<DrawerContainerSelectorProps> = ({
  selectedContainer,
  setSelectedContainer,
  pythonVersion,
  setPythonVersion,
  cudaVersion,
  setCudaVersion,
  closeDrawer,
  lifecycleScriptTab,
  setLifecycleScriptTab,
  lifecycleScriptFile,
  setLifecycleScriptFile,
  autoFocusScript,
  selectedGpuName,
  selectedInstanceArchitecture,
  showRapidsOption,
  disableKubernetes,
  kubernetesDisabledReason,
}) => {
  const deprecateFeaturedContainers =
    useIdentityFeatureFlagEnabled(
      POSTHOG_IDENTITY_FLAGS.HIDE_FEATURED_CONTAINERS,
    ) === true;
  const isArmInstance = isArmArchitecture(selectedInstanceArchitecture);
  const [localSelectedContainer, setLocalSelectedContainer] =
    useState<ContainerSelected>(selectedContainer);
  const [localPythonVersion, setLocalPythonVersion] =
    useState<string>(pythonVersion);
  const [localCudaVersion, setLocalCudaVersion] = useState<string>(cudaVersion);
  const [localLifecycleScriptTab, setLocalLifecycleScriptTab] = useState<
    "upload" | "paste"
  >(lifecycleScriptTab);
  const [localLifecycleScriptFile, setLocalLifecycleScriptFile] =
    useState<File | null>(lifecycleScriptFile);

  const handleApply = (
    containerToApply: ContainerSelected,
    featuredContainerParams?: FeaturedContainerVersionParams,
  ) => {
    if (containerToApply.verbBuild?.containerUrl === "Default") {
      const nextPythonVersion =
        featuredContainerParams?.pythonVersion ?? localPythonVersion;
      const nextCudaVersion =
        featuredContainerParams?.cudaVersion ?? localCudaVersion;
      setCudaVersion(nextCudaVersion);
      setPythonVersion(nextPythonVersion);
      setSelectedContainer(containerToApply, {
        pythonVersion: nextPythonVersion,
        cudaVersion: nextCudaVersion,
      });
    } else {
      setSelectedContainer(containerToApply);
    }
    if (containerToApply.vmBuild) {
      // Save local lifecycle script UI states to parent
      setLifecycleScriptTab(localLifecycleScriptTab);
      setLifecycleScriptFile(localLifecycleScriptFile);
    }
    closeDrawer();
  };

  return (
    <div>
      <BaseContainerSelector
        selectedContainer={localSelectedContainer}
        onContainerChange={setLocalSelectedContainer}
        pythonVersion={localPythonVersion}
        onPythonVersionChange={setLocalPythonVersion}
        cudaVersion={localCudaVersion}
        onCudaVersionChange={setLocalCudaVersion}
        lifecycleScriptTab={localLifecycleScriptTab}
        onLifecycleScriptTabChange={setLocalLifecycleScriptTab}
        lifecycleScriptFile={localLifecycleScriptFile}
        onLifecycleScriptFileChange={setLocalLifecycleScriptFile}
        showApplyButton
        onApply={handleApply}
        applyButtonText="Apply"
        showCustomContainer={true}
        gridCols="grid-cols-1 sm:grid-cols-2"
        autoFocusScript={autoFocusScript}
        selectedGpuName={selectedGpuName}
        showRapidsOption={showRapidsOption}
        singleContainerCustomOnly={deprecateFeaturedContainers}
        isArmInstance={isArmInstance}
        disableKubernetes={disableKubernetes}
        kubernetesDisabledReason={kubernetesDisabledReason}
      />
    </div>
  );
};

export default DrawerContainerSelector;
